import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { fetchPersonalizationFonts, fetchPrintSourceUrl } from "../api/DesignAPIs";
import { fetchNameSurcharge } from "../api/CategoryAPIs";
import { API_BASE_URL_V2 } from "../api/URL_Header";

const PREVIEW_ZOOM_MIN = 25;
const PREVIEW_ZOOM_MAX = 140;
const PREVIEW_ZOOM_STEP = 10;
const PREVIEW_ZOOM_DEFAULT = 50;
const FONT_SIZE_MAX = 60;
const EDGE_EXCLUSION = { leftPercent: 8, rightPercent: 8, bottomPercent: 10 };

export const PERSONALIZATION_FONTS = [
  { id: "cairo-regular", name: "Cairo Regular", cssFamily: "Cairo", weight: 400, supportsArabic: true, supportsEnglish: true },
  { id: "cairo-bold", name: "Cairo Bold", cssFamily: "Cairo", weight: 700, supportsArabic: true, supportsEnglish: true },
  { id: "cairo-black", name: "Cairo Black", cssFamily: "Cairo", weight: 900, supportsArabic: true, supportsEnglish: true },
  { id: "tajawal-bold", name: "Tajawal Bold", cssFamily: "Tajawal", weight: 700, supportsArabic: true, supportsEnglish: true },
  { id: "amiri-regular", name: "Amiri", cssFamily: "Amiri", weight: 400, supportsArabic: true, supportsEnglish: true },
  { id: "amiri-bold", name: "Amiri Bold", cssFamily: "Amiri", weight: 700, supportsArabic: true, supportsEnglish: true },
  { id: "battekh-1", name: "Battekh-1", cssFamily: "Battekh", weight: 700, supportsArabic: true, supportsEnglish: false },
  { id: "montserrat-bold", name: "Montserrat Bold", cssFamily: "Montserrat", weight: 700, supportsArabic: false, supportsEnglish: true },
];

export const DEFAULT_LAYER_COLORS = {
  ar: [
    { id: "white", name: "أبيض", hex: "#FFFFFF", enabled: true },
    { id: "black", name: "أسود", hex: "#111111", enabled: true },
    { id: "red", name: "أحمر", hex: "#C41E3A", enabled: true },
    { id: "gold", name: "ذهبي", hex: "#C9A227", enabled: true },
  ],
  en: [
    { id: "white", name: "White", hex: "#FFFFFF", enabled: true },
    { id: "black", name: "Black", hex: "#111111", enabled: true },
    { id: "green", name: "Green", hex: "#15803D", enabled: true },
    { id: "blue", name: "Blue", hex: "#1D4ED8", enabled: true },
  ],
};

const fontFileHref = (font) => {
  const path = font?.fileUrl || font?.file || "";
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${API_BASE_URL_V2}${path.startsWith("/") ? "" : "/"}${path}`;
};

const loadedAdminFonts = new Map();

function loadAdminFont(font) {
  if (!font || typeof document === "undefined" || !document.fonts) return Promise.resolve();
  const family = font.webFamily || font.cssFamily;
  const href = fontFileHref(font);
  if (!family) return Promise.resolve();
  if (loadedAdminFonts.has(family)) return loadedAdminFonts.get(family);
  if (!href) return Promise.resolve();
  const format = font.format ? ` format("${font.format}")` : "";
  const face = new FontFace(family, `url("${href}")${format}`, { style: "normal", weight: String(font.weight || 400) });
  const promise = face.load().then((loaded) => {
    document.fonts.add(loaded);
    return family;
  }).catch(() => {
    loadedAdminFonts.delete(family);
  });
  loadedAdminFonts.set(family, promise);
  return promise;
}

const LAYER_META = {
  ar: { key: "ar", label: "عربي", script: "arabic", direction: "rtl", sample: "محمد" },
  en: { key: "en", label: "English", script: "latin", direction: "ltr", sample: "Mohamed" },
};

export function createOptionalDefaultLayer(key, fonts = PERSONALIZATION_FONTS) {
  const base = createDefaultLayer(key);
  const arabic = base.script === "arabic";
  const catalog = fonts?.length ? fonts : PERSONALIZATION_FONTS;
  const compatible = catalog.filter((font) => (arabic ? font.supportsArabic !== false : font.supportsEnglish !== false));
  const allowedFontIds = [];
  const push = (id) => {
    const value = String(id || "").trim();
    if (!value || allowedFontIds.includes(value)) return;
    allowedFontIds.push(value);
  };
  push(base.fontId);
  compatible.forEach((font) => push(font.id));
  return {
    ...base,
    enabled: arabic,
    allowCustomerFont: true,
    allowedFontIds: allowedFontIds.length ? allowedFontIds : [base.fontId],
    allowCustomerFontSize: true,
    fontSizePercent: 16,
    posY: 38,
    minFontSizePercent: 10,
    maxFontSizePercent: 50,
    allowCustomerMove: true,
    allowCustomColor: true,
  };
}

export function createOptionalDefaultLayers(fonts = PERSONALIZATION_FONTS) {
  return [createOptionalDefaultLayer("ar", fonts), createOptionalDefaultLayer("en", fonts)];
}

export function createDefaultLayer(key, overrides = {}) {
  const meta = LAYER_META[key] || { key, label: key, script: "latin", direction: "ltr", sample: "Text" };
  const arabic = meta.script === "arabic";
  return {
    key: meta.key,
    label: meta.label,
    enabled: true,
    script: meta.script,
    direction: meta.direction,
    fontId: arabic ? "cairo-bold" : "montserrat-bold",
    fontSizePercent: 8,
    textColor: "#FFFFFF",
    posX: 50,
    posY: arabic ? 78 : 88,
    boxWidthPercent: 50,
    boxHeightPercent: 0,
    alignH: "center",
    alignV: "middle",
    rotation: 0,
    letterSpacing: 0,
    maxLength: 12,
    minLength: 1,
    zIndex: arabic ? 1 : 2,
    colors: (DEFAULT_LAYER_COLORS[meta.key] || DEFAULT_LAYER_COLORS.en).map((color) => ({ ...color })),
    allowCustomerFont: false,
    allowedFontIds: [arabic ? "cairo-bold" : "montserrat-bold"],
    allowCustomerFontSize: false,
    minFontSizePercent: 8,
    maxFontSizePercent: 8,
    allowCustomerMove: false,
    allowCustomColor: true,
    ...overrides,
  };
}

export function defaultLayers() {
  return [createDefaultLayer("ar"), createDefaultLayer("en")];
}

function clamp(value, min, max, fallback) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  if (num < min) return min;
  if (num > max) return max;
  return num;
}

function normalizeHex(value, fallback = "#FFFFFF") {
  let hex = String(value || "").trim();
  if (!hex) return fallback;
  if (!hex.startsWith("#")) hex = `#${hex}`;
  if (hex.length === 4) hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  return /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex.toUpperCase() : fallback;
}

function normalizeLayer(layer = {}, index = 0) {
  const key = String(layer.key || layer.Key || (index === 0 ? "ar" : "en")).toLowerCase();
  const meta = LAYER_META[key] || LAYER_META.en;
  const arabic = meta.script === "arabic";
  const colors = Array.isArray(layer.colors || layer.Colors)
    ? (layer.colors || layer.Colors).map((color, colorIndex) => ({
        id: String(color.id || color.Id || `c-${colorIndex}`),
        name: color.name || color.Name || (arabic ? "لون" : "Color"),
        hex: normalizeHex(color.hex || color.Hex),
        enabled: color.enabled !== false && color.Enabled !== false,
      }))
    : (DEFAULT_LAYER_COLORS[meta.key] || DEFAULT_LAYER_COLORS.en).map((color) => ({ ...color }));
  const textColor = normalizeHex(layer.textColor || layer.TextColor, colors.find((c) => c.enabled)?.hex || "#FFFFFF");
  if (!colors.some((color) => color.hex === textColor)) {
    colors.unshift({ id: "default", name: arabic ? "افتراضي" : "Default", hex: textColor, enabled: true });
  }
  return {
    key: meta.key,
    label: layer.label || layer.Label || meta.label,
    enabled: layer.enabled !== false && layer.Enabled !== false,
    script: meta.script,
    direction: String(layer.direction || layer.Direction || meta.direction).toLowerCase(),
    fontId: layer.fontId || layer.FontId || (arabic ? "cairo-bold" : "montserrat-bold"),
    fontSizePercent: clamp(layer.fontSizePercent ?? layer.FontSizePercent, 1, FONT_SIZE_MAX, 8),
    textColor,
    posX: clamp(layer.posX ?? layer.PosX, 0, 100, 50),
    posY: clamp(layer.posY ?? layer.PosY, 0, 100, arabic ? 78 : 88),
    boxWidthPercent: clamp(layer.boxWidthPercent ?? layer.BoxWidthPercent, 8, 100, 50),
    boxHeightPercent: clamp(layer.boxHeightPercent ?? layer.BoxHeightPercent, 0, 100, 0),
    alignH: String(layer.alignH || layer.AlignH || "center").toLowerCase(),
    alignV: String(layer.alignV || layer.AlignV || "middle").toLowerCase(),
    rotation: clamp(layer.rotation ?? layer.Rotation, -180, 180, 0),
    letterSpacing: clamp(layer.letterSpacing ?? layer.LetterSpacing, -10, 40, 0),
    maxLength: clamp(layer.maxLength ?? layer.MaxLength, 1, 40, 12),
    minLength: clamp(layer.minLength ?? layer.MinLength, 0, 20, 1),
    zIndex: clamp(layer.zIndex ?? layer.ZIndex, 1, 100, arabic ? 1 : 2),
    colors,
    allowCustomerFont: Boolean(layer.allowCustomerFont ?? layer.AllowCustomerFont),
    allowedFontIds: Boolean(layer.allowCustomerFont ?? layer.AllowCustomerFont)
      ? normalizeAllowedFontIds(
          layer.allowedFontIds || layer.AllowedFontIds || layer.allowedFonts || layer.AllowedFonts,
          layer.fontId || layer.FontId || (arabic ? "cairo-bold" : "montserrat-bold")
        )
      : [layer.fontId || layer.FontId || (arabic ? "cairo-bold" : "montserrat-bold")],
    allowCustomerFontSize: Boolean(layer.allowCustomerFontSize ?? layer.AllowCustomerFontSize),
    minFontSizePercent: Boolean(layer.allowCustomerFontSize ?? layer.AllowCustomerFontSize)
      ? clamp(layer.minFontSizePercent ?? layer.MinFontSizePercent, 1, FONT_SIZE_MAX, clamp(layer.fontSizePercent ?? layer.FontSizePercent, 1, FONT_SIZE_MAX, 8))
      : clamp(layer.fontSizePercent ?? layer.FontSizePercent, 1, FONT_SIZE_MAX, 8),
    maxFontSizePercent: Boolean(layer.allowCustomerFontSize ?? layer.AllowCustomerFontSize)
      ? clamp(layer.maxFontSizePercent ?? layer.MaxFontSizePercent, 1, FONT_SIZE_MAX, clamp(layer.fontSizePercent ?? layer.FontSizePercent, 1, FONT_SIZE_MAX, 8))
      : clamp(layer.fontSizePercent ?? layer.FontSizePercent, 1, FONT_SIZE_MAX, 8),
    allowCustomerMove: Boolean(layer.allowCustomerMove ?? layer.AllowCustomerMove),
    allowCustomColor: Boolean(layer.allowCustomColor ?? layer.AllowCustomColor),
  };
}

function normalizeAllowedFontIds(raw, defaultId) {
  const ids = [];
  const push = (id) => {
    const value = String(id || "").trim();
    if (!value || ids.includes(value)) return;
    ids.push(value);
  };
  if (Array.isArray(raw)) {
    raw.forEach((item) => push(typeof item === "string" ? item : item?.id || item?.Id || item?.fontId));
  }
  push(defaultId);
  if (ids[0] !== defaultId) {
    const filtered = ids.filter((id) => id !== defaultId);
    return [defaultId, ...filtered];
  }
  return ids.length ? ids : [defaultId];
}

function synthesizeLegacyLayers(design = {}) {
  const wantsAr = design.requiresArabicName || design.RequiresArabicName || !(design.requiresEnglishName || design.RequiresEnglishName);
  const wantsEn = design.requiresEnglishName || design.RequiresEnglishName || !(design.requiresArabicName || design.RequiresArabicName);
  const shared = {
    fontId: design.personalizationFont || "cairo-bold",
    fontSizePercent: design.personalizationFontSize || 8,
    textColor: design.personalizationTextColor || "#FFFFFF",
    posX: design.personalizationPosX ?? 50,
    posY: design.personalizationPosY ?? 82,
    alignH: design.personalizationAlignH || "center",
    alignV: design.personalizationAlignV || "middle",
    rotation: design.personalizationRotation || 0,
    maxLength: design.maxNameLength || 12,
    minLength: design.minNameLength || 1,
  };
  const layers = [];
  if (wantsAr !== false) {
    layers.push(createDefaultLayer("ar", { ...shared, posY: shared.posY }));
  }
  if (wantsEn !== false) {
    layers.push(createDefaultLayer("en", {
      ...shared,
      fontId: PERSONALIZATION_FONTS.find((font) => font.id === shared.fontId)?.supportsEnglish
        ? shared.fontId
        : "montserrat-bold",
      posY: Math.min(100, Number(shared.posY) + (wantsAr !== false ? 10 : 0)),
    }));
  }
  return layers.length ? layers : defaultLayers();
}

function cameraSettingsFrom(source = {}) {
  const cfg = source.personalizationConfig || source.PersonalizationConfig || {};
  const enabled = Boolean(
    source.cameraExclusionEnabled ?? source.CameraExclusionEnabled ?? cfg.cameraExclusionEnabled ?? cfg.CameraExclusionEnabled
  );
  return {
    cameraExclusionEnabled: enabled,
    cameraExclusionHeightPercent: clamp(
      source.cameraExclusionHeightPercent ?? source.CameraExclusionHeightPercent ?? cfg.cameraExclusionHeightPercent ?? cfg.CameraExclusionHeightPercent,
      4,
      40,
      15
    ),
    cameraExclusionTopPercent: clamp(
      source.cameraExclusionTopPercent ?? source.CameraExclusionTopPercent ?? cfg.cameraExclusionTopPercent ?? cfg.CameraExclusionTopPercent,
      0,
      40,
      0
    ),
    previewCornerRadiusPercent: clamp(
      source.previewCornerRadiusPercent ?? source.PreviewCornerRadiusPercent ?? cfg.previewCornerRadiusPercent ?? cfg.PreviewCornerRadiusPercent,
      0,
      28,
      12
    ),
  };
}

export function syncLegacyFromLayers(layers, extras = {}) {
  const normalized = (layers || []).map(normalizeLayer);
  const ar = normalized.find((layer) => layer.key === "ar");
  const en = normalized.find((layer) => layer.key === "en");
  const primary = normalized.find((layer) => layer.enabled) || ar || normalized[0];
  const enabled = normalized.filter((layer) => layer.enabled);
  const camera = cameraSettingsFrom(extras);
  return {
    personalizationLayers: normalized,
    personalizationConfig: {
      version: 2,
      layers: normalized,
      cameraExclusionEnabled: camera.cameraExclusionEnabled,
      cameraExclusionHeightPercent: camera.cameraExclusionHeightPercent,
      cameraExclusionTopPercent: camera.cameraExclusionTopPercent,
      previewCornerRadiusPercent: camera.previewCornerRadiusPercent,
    },
    ...camera,
    requiresArabicName: ar?.enabled !== false && Boolean(ar),
    requiresEnglishName: en?.enabled !== false && Boolean(en),
    personalizationFont: primary?.fontId || "cairo-bold",
    personalizationFontWeight: "bold",
    personalizationFontSize: primary?.fontSizePercent ?? 8,
    personalizationTextColor: primary?.textColor || "#FFFFFF",
    personalizationPosX: primary?.posX ?? 50,
    personalizationPosY: primary?.posY ?? 82,
    personalizationAlignH: primary?.alignH || "center",
    personalizationAlignV: primary?.alignV || "middle",
    personalizationRotation: primary?.rotation || 0,
    personalizationTextDirection: primary?.direction || "auto",
    maxNameLength: enabled.length ? Math.max(...enabled.map((layer) => layer.maxLength || 12)) : 12,
    minNameLength: enabled.length ? Math.min(...enabled.map((layer) => layer.minLength ?? 1)) : 1,
  };
}

export function hydratePersonalizationState(design = {}) {
  const allowsPersonalization = Boolean(design.allowsPersonalization || design.AllowsPersonalization);
  const optionalPersonalization = !allowsPersonalization && Boolean(design.optionalPersonalization || design.OptionalPersonalization);
  const raw = design.personalizationLayers || design.PersonalizationLayers
    || design.personalizationConfig?.layers || design.PersonalizationConfig?.layers;
  const usesFlag = design.personalizationUsesDefaults ?? design.PersonalizationUsesDefaults;
  const personalizationUsesDefaults = (allowsPersonalization || optionalPersonalization) && usesFlag !== false && (
    usesFlag === true || !(Array.isArray(raw) && raw.length)
  );
  const layers = Array.isArray(raw) && raw.length
    ? raw.map(normalizeLayer)
    : personalizationUsesDefaults
    ? createOptionalDefaultLayers()
    : synthesizeLegacyLayers(design).map(normalizeLayer);
  return {
    ...syncLegacyFromLayers(layers, design),
    allowsPersonalization,
    optionalPersonalization,
    personalizationUsesDefaults,
    nameSurcharge: design.nameSurcharge === 0 || design.nameSurcharge
      ? String(design.nameSurcharge)
      : design.NameSurcharge === 0 || design.NameSurcharge
      ? String(design.NameSurcharge)
      : "",
  };
}

export const REGULAR_DESIGN_PRICE = 250;
export const NAME_DESIGN_PRICE = 300;

export const defaultPersonalization = {
  ...syncLegacyFromLayers(defaultLayers()),
  allowsPersonalization: false,
  optionalPersonalization: false,
  personalizationUsesDefaults: true,
  nameSurcharge: "",
};

function priceForDesignType(personalized, current) {
  const num = Number(current);
  const previousDefault = personalized ? REGULAR_DESIGN_PRICE : NAME_DESIGN_PRICE;
  const nextDefault = personalized ? NAME_DESIGN_PRICE : REGULAR_DESIGN_PRICE;
  if (!Number.isFinite(num) || num === 0 || num === previousDefault) return nextDefault;
  return num;
}

const translateX = { left: "0%", center: "-50%", right: "-100%" };
const translateY = { top: "0%", middle: "-50%", bottom: "-100%" };

const LayerFields = ({ layer, onChange, fonts }) => {
  const arabic = layer.script === "arabic";
  const catalog = fonts?.length ? fonts : PERSONALIZATION_FONTS;
  let filtered = catalog.filter((font) => (arabic ? font.supportsArabic : font.supportsEnglish));
  if (layer.fontId && !filtered.some((font) => font.id === layer.fontId)) {
    const current = catalog.find((font) => font.id === layer.fontId);
    if (current) filtered = [current, ...filtered];
  }
  const field = (label, children) => (
    <div className="space-y-1">
      <label className="block text-right text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
  const inputClass = "w-full p-2.5 border-2 border-gray-200 rounded-xl bg-white";

  const moveColor = (index, direction) => {
    const next = [...layer.colors];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange({ colors: next });
  };

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={layer.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
          className="w-5 h-5 text-purple-600 rounded"
        />
        <span className="font-bold text-purple-800">تفعيل طبقة {layer.label}</span>
      </label>

      {layer.enabled && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {field("الخط الافتراضي", (
              <select
                value={layer.fontId}
                onChange={(e) => {
                  const fontId = e.target.value;
                  const rest = (layer.allowedFontIds || []).filter((id) => id !== fontId);
                  onChange({ fontId, allowedFontIds: [fontId, ...rest] });
                }}
                className={inputClass}
              >
                {filtered.map((font) => (
                  <option key={font.id} value={font.id} style={{ fontFamily: font.cssFamily || font.webFamily }}>{font.name}</option>
                ))}
              </select>
            ))}
            {field("حجم الخط الافتراضي (% من عرض الصورة)", (
              <input
                type="number"
                min="1"
                max={FONT_SIZE_MAX}
                step="0.1"
                value={layer.fontSizePercent}
                onChange={(e) => {
                  const fontSizePercent = Number(e.target.value);
                  onChange(layer.allowCustomerFontSize
                    ? { fontSizePercent }
                    : { fontSizePercent, minFontSizePercent: fontSizePercent, maxFontSizePercent: fontSizePercent });
                }}
                className={inputClass}
              />
            ))}
            {field("اللون الافتراضي", (
              <div className="flex items-center gap-2">
                <input type="color" value={layer.textColor}
                  onChange={(e) => onChange({ textColor: e.target.value.toUpperCase() })} className="h-11 w-16 border-2 border-gray-200 rounded-xl" />
                <input value={layer.textColor} onChange={(e) => onChange({ textColor: normalizeHex(e.target.value) })} className={inputClass} />
              </div>
            ))}
            {field("المحاذاة الأفقية", (
              <select value={layer.alignH} onChange={(e) => onChange({ alignH: e.target.value })} className={inputClass}>
                <option value="left">يسار</option>
                <option value="center">وسط</option>
                <option value="right">يمين</option>
              </select>
            ))}
            {field("المحاذاة الرأسية", (
              <select value={layer.alignV} onChange={(e) => onChange({ alignV: e.target.value })} className={inputClass}>
                <option value="top">أعلى</option>
                <option value="middle">وسط</option>
                <option value="bottom">أسفل</option>
              </select>
            ))}
            {field("X %", (
              <input type="number" min="0" max="100" step="0.1" value={layer.posX}
                onChange={(e) => onChange({ posX: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("Y %", (
              <input type="number" min="0" max="100" step="0.1" value={layer.posY}
                onChange={(e) => onChange({ posY: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("العرض %", (
              <input type="number" min="8" max="100" step="1" value={layer.boxWidthPercent}
                onChange={(e) => onChange({ boxWidthPercent: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("الارتفاع % (0 = تلقائي)", (
              <input type="number" min="0" max="100" step="1" value={layer.boxHeightPercent}
                onChange={(e) => onChange({ boxHeightPercent: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("الدوران", (
              <input type="number" min="-180" max="180" step="1" value={layer.rotation}
                onChange={(e) => onChange({ rotation: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("تباعد الحروف", (
              <input type="number" min="-10" max="40" step="0.1" value={layer.letterSpacing}
                onChange={(e) => onChange({ letterSpacing: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("اتجاه النص", (
              <select value={layer.direction} onChange={(e) => onChange({ direction: e.target.value })} className={inputClass}>
                <option value="rtl">RTL</option>
                <option value="ltr">LTR</option>
              </select>
            ))}
            {field("الحد الأقصى", (
              <input type="number" min="1" max="40" value={layer.maxLength}
                onChange={(e) => onChange({ maxLength: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("الحد الأدنى", (
              <input type="number" min="0" max="20" value={layer.minLength}
                onChange={(e) => onChange({ minLength: Number(e.target.value) })} className={inputClass} />
            ))}
          </div>

          <div className="rounded-2xl border border-purple-200 bg-white p-3 space-y-3">
            <p className="font-bold text-purple-800">خط العميل</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(layer.allowCustomerFont)}
                onChange={(e) => onChange({
                  allowCustomerFont: e.target.checked,
                  allowedFontIds: e.target.checked
                    ? (layer.allowedFontIds?.length ? layer.allowedFontIds : [layer.fontId])
                    : [layer.fontId],
                })}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="font-medium text-gray-800">السماح للعميل يغيّر الخط</span>
            </label>
            {layer.allowCustomerFont ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">اختار الخطوط المتاحة للعميل. الخط الافتراضي ظاهر دايمًا.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {filtered.map((font) => {
                    const checked = (layer.allowedFontIds || []).includes(font.id);
                    const isDefault = font.id === layer.fontId;
                    return (
                      <label
                        key={font.id}
                        className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${checked ? "border-purple-300 bg-purple-50" : "border-gray-200 bg-white"}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked || isDefault}
                          disabled={isDefault}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...new Set([layer.fontId, ...(layer.allowedFontIds || []), font.id])]
                              : [layer.fontId, ...(layer.allowedFontIds || []).filter((id) => id !== font.id)];
                            onChange({ allowedFontIds: next });
                          }}
                          className="w-4 h-4 text-purple-600 rounded"
                        />
                        <span style={{ fontFamily: `"${font.webFamily || font.cssFamily}", sans-serif` }} className="text-sm">
                          {font.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">العميل هيستخدم الخط الافتراضي فقط.</p>
            )}
          </div>

          <div className="rounded-2xl border border-purple-200 bg-white p-3 space-y-3">
            <p className="font-bold text-purple-800">حجم خط العميل</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(layer.allowCustomerFontSize)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const size = Number(layer.fontSizePercent) || 8;
                  onChange({
                    allowCustomerFontSize: checked,
                    minFontSizePercent: checked ? Math.max(1, Math.round((size - 4) * 10) / 10) : size,
                    maxFontSizePercent: checked ? Math.min(FONT_SIZE_MAX, Math.round((size + 8) * 10) / 10) : size,
                  });
                }}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="font-medium text-gray-800">السماح للعميل يغيّر حجم الخط</span>
            </label>
            {layer.allowCustomerFontSize ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field("الحد الأدنى %", (
                  <input
                    type="number"
                    min="1"
                    max={FONT_SIZE_MAX}
                    step="0.1"
                    value={layer.minFontSizePercent}
                    onChange={(e) => {
                      const minFontSizePercent = Math.max(1, Number(e.target.value) || 1);
                      const maxFontSizePercent = Math.max(minFontSizePercent, Number(layer.maxFontSizePercent) || minFontSizePercent);
                      const fontSizePercent = Math.min(maxFontSizePercent, Math.max(minFontSizePercent, Number(layer.fontSizePercent) || minFontSizePercent));
                      onChange({ minFontSizePercent, maxFontSizePercent, fontSizePercent });
                    }}
                    className={inputClass}
                  />
                ))}
                {field("الحد الأقصى %", (
                  <input
                    type="number"
                    min="1"
                    max={FONT_SIZE_MAX}
                    step="0.1"
                    value={layer.maxFontSizePercent}
                    onChange={(e) => {
                      const maxFontSizePercent = Math.max(Number(layer.minFontSizePercent) || 1, Number(e.target.value) || 1);
                      const fontSizePercent = Math.min(maxFontSizePercent, Math.max(Number(layer.minFontSizePercent) || 1, Number(layer.fontSizePercent) || 8));
                      onChange({ maxFontSizePercent, fontSizePercent });
                    }}
                    className={inputClass}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">العميل هيستخدم الحجم الافتراضي فقط ({layer.fontSizePercent}%).</p>
            )}
          </div>

          <div className="rounded-2xl border border-purple-200 bg-white p-3 space-y-3">
            <p className="font-bold text-purple-800">مكان النص</p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(layer.allowCustomerMove)}
                onChange={(e) => onChange({ allowCustomerMove: e.target.checked })}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="font-medium text-gray-800">السماح للعميل يحرّك النص</span>
            </label>
            {layer.allowCustomerMove ? (
              <p className="text-sm text-gray-600">العميل هيقدر يسحب الاسم على الصورة عشان يعدّل مكانه بعد تغيير الخط. المكان الافتراضي هو X {layer.posX}% و Y {layer.posY}%.</p>
            ) : (
              <p className="text-sm text-gray-500">العميل هيستخدم المكان الافتراضي فقط (X {layer.posX}% / Y {layer.posY}%).</p>
            )}
          </div>

          <div className="rounded-2xl border border-purple-200 bg-white p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="font-bold text-purple-800">ألوان العميل المتاحة</p>
              <button
                type="button"
                onClick={() => onChange({
                  colors: [...layer.colors, { id: `c-${Date.now()}`, name: arabic ? "لون جديد" : "New color", hex: "#FFFFFF", enabled: true }],
                })}
                className="px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm font-semibold"
              >
                إضافة لون
              </button>
            </div>
            <div className="space-y-2">
              {layer.colors.map((color, index) => (
                <div key={color.id || index} className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center bg-purple-50/70 p-2 rounded-xl">
                  <input
                    type="color"
                    value={color.hex}
                    onChange={(e) => {
                      const colors = layer.colors.map((item, itemIndex) => itemIndex === index ? { ...item, hex: e.target.value.toUpperCase() } : item);
                      onChange({ colors, textColor: index === 0 ? e.target.value.toUpperCase() : layer.textColor });
                    }}
                    className="h-10 w-12 border border-gray-200 rounded-lg"
                  />
                  <input
                    value={color.name}
                    onChange={(e) => onChange({
                      colors: layer.colors.map((item, itemIndex) => itemIndex === index ? { ...item, name: e.target.value } : item),
                    })}
                    className="p-2 border border-gray-200 rounded-lg"
                    placeholder={arabic ? "اسم اللون" : "Color name"}
                  />
                  <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={color.enabled}
                      onChange={(e) => onChange({
                        colors: layer.colors.map((item, itemIndex) => itemIndex === index ? { ...item, enabled: e.target.checked } : item),
                      })}
                    />
                    ظاهر
                  </label>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => moveColor(index, -1)} className="px-2 py-1 rounded bg-white border text-sm">↑</button>
                    <button type="button" onClick={() => moveColor(index, 1)} className="px-2 py-1 rounded bg-white border text-sm">↓</button>
                    <button
                      type="button"
                      onClick={() => onChange({ colors: layer.colors.filter((_, itemIndex) => itemIndex !== index) })}
                      className="px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 text-sm"
                    >
                      حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const DesignPersonalizationFields = ({
  state,
  setState,
  imageUrl,
  mockupImageUrl,
  children,
}) => {
  const mockupUrl = mockupImageUrl || imageUrl || "";
  const showNameSettings = Boolean(state.allowsPersonalization || state.optionalPersonalization);
  const [printSourceUrl, setPrintSourceUrl] = useState("");
  const [globalNameSurcharge, setGlobalNameSurcharge] = useState(50);
  useEffect(() => {
    let cancelled = false;
    fetchNameSurcharge()
      .then((amount) => {
        if (!cancelled && Number.isFinite(Number(amount))) setGlobalNameSurcharge(Number(amount));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    const code = String(state.code || "").trim();
    if (!showNameSettings || !code || code.toLowerCase() === "none") {
      setPrintSourceUrl("");
      return undefined;
    }
    let cancelled = false;
    fetchPrintSourceUrl(code)
      .then((url) => {
        if (!cancelled) setPrintSourceUrl(url || "");
      })
      .catch(() => {
        if (!cancelled) setPrintSourceUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [state.code, showNameSettings]);
  const resolvedPrintSource = printSourceUrl
    ? (printSourceUrl.startsWith("http") ? printSourceUrl : `${API_BASE_URL_V2}${printSourceUrl}`)
    : "";
  const canvasUrl = showNameSettings && resolvedPrintSource ? resolvedPrintSource : mockupUrl;
  const [fontCatalog, setFontCatalog] = useState(PERSONALIZATION_FONTS);
  const previewRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(PREVIEW_ZOOM_DEFAULT);
  const layers = useMemo(
    () => (state.personalizationLayers?.length ? state.personalizationLayers : defaultLayers()).map(normalizeLayer),
    [state.personalizationLayers]
  );
  const enabledLayers = useMemo(
    () => [...layers.filter((layer) => layer.enabled)].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)),
    [layers]
  );
  const stackedFrontToBack = useMemo(
    () => [...enabledLayers].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0)),
    [enabledLayers]
  );
  const [activeKey, setActiveKey] = useState(() => enabledLayers[0]?.key || "ar");
  const [samples, setSamples] = useState({ ar: "محمد", en: "Mohamed" });
  const active = layers.find((layer) => layer.key === activeKey) || enabledLayers[0] || layers[0];
  const topLayerKey = stackedFrontToBack[0]?.key;

  useEffect(() => {
    let cancelled = false;
    fetchPersonalizationFonts()
      .then((fonts) => {
        if (!cancelled && fonts.length) setFontCatalog(fonts);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fontCatalog.forEach(loadAdminFont);
    layers.forEach((layer) => {
      const font = fontCatalog.find((item) => item.id === layer.fontId);
      if (font) loadAdminFont(font);
    });
  }, [fontCatalog, layers]);

  useLayoutEffect(() => {
    const node = previewRef.current;
    if (!node) return undefined;
    const updateWidth = () => {
      const next = node.clientWidth;
      if (next > 0) setPreviewWidth(next);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [canvasUrl, previewZoom]);

  const applyLayers = (nextLayers, { customize = false } = {}) => setState({
    ...state,
    ...syncLegacyFromLayers(nextLayers, state),
    allowsPersonalization: state.allowsPersonalization,
    optionalPersonalization: state.optionalPersonalization,
    personalizationUsesDefaults: customize ? false : state.personalizationUsesDefaults,
  });
  const updateLayer = (key, patch) => applyLayers(
    layers.map((layer) => (layer.key === key ? { ...layer, ...patch } : layer)),
    { customize: true }
  );

  const setTopLayer = (key) => {
    const max = Math.max(...layers.map((layer) => Number(layer.zIndex) || 0), 0);
    applyLayers(layers.map((layer) => ({
      ...layer,
      zIndex: layer.key === key ? max + 1 : Math.max(1, Number(layer.zIndex) || 1),
    })), { customize: true });
  };

  const setNameMode = (mode) => {
    const required = mode === "required";
    const optional = mode === "optional";
    const named = required || optional;
    const alreadyNamed = Boolean(state.allowsPersonalization || state.optionalPersonalization);
    const keepOverride = named && alreadyNamed && state.personalizationUsesDefaults === false && state.personalizationLayers?.length;
    const nextLayers = named
      ? (keepOverride ? layers : createOptionalDefaultLayers(fontCatalog))
      : layers;
    setState({
      ...state,
      allowsPersonalization: required,
      optionalPersonalization: optional,
      personalizationUsesDefaults: named ? !keepOverride : true,
      oldPrice: priceForDesignType(required, state.oldPrice),
      newPrice: priceForDesignType(required, state.newPrice),
      ...(named
        ? syncLegacyFromLayers(nextLayers, {
            ...state,
            cameraExclusionEnabled: keepOverride ? state.cameraExclusionEnabled : true,
            cameraExclusionHeightPercent: state.cameraExclusionHeightPercent || 15,
            cameraExclusionTopPercent: keepOverride ? state.cameraExclusionTopPercent : 0,
            previewCornerRadiusPercent: state.previewCornerRadiusPercent ?? 12,
          })
        : {}),
    });
  };

  const resetOptionalDefaults = () => {
    const nextLayers = createOptionalDefaultLayers(fontCatalog);
    setState({
      ...state,
      allowsPersonalization: state.allowsPersonalization,
      optionalPersonalization: state.optionalPersonalization,
      personalizationUsesDefaults: true,
      ...syncLegacyFromLayers(nextLayers, {
        ...state,
        cameraExclusionEnabled: true,
        cameraExclusionHeightPercent: 15,
        cameraExclusionTopPercent: 0,
        previewCornerRadiusPercent: state.previewCornerRadiusPercent ?? 12,
      }),
    });
  };

  const handlePreviewClick = (event) => {
    if (!active?.enabled) return;
    const rect = event.currentTarget.getBoundingClientRect();
    let x = ((event.clientX - rect.left) / rect.width) * 100;
    let y = ((event.clientY - rect.top) / rect.height) * 100;
    const fontSize = Number(active.fontSizePercent) || 8;
    const halfHeight = (fontSize * 1.4 * (rect.width / Math.max(rect.height, 1))) / 2;
    if (state.cameraExclusionEnabled) {
      const bottom = Math.min(
        100,
        clamp(state.cameraExclusionTopPercent, 0, 40, 0) + clamp(state.cameraExclusionHeightPercent, 4, 40, 15)
      );
      if (y - halfHeight < bottom) y = bottom + halfHeight;
    }
    const floor = 100 - EDGE_EXCLUSION.bottomPercent;
    if (y + halfHeight > floor) y = floor - halfHeight;
    x = Math.min(100 - EDGE_EXCLUSION.rightPercent, Math.max(EDGE_EXCLUSION.leftPercent, x));
    y = Math.min(100, Math.max(0, y));
    updateLayer(active.key, {
      posX: Math.round(Math.min(100, Math.max(0, x)) * 10) / 10,
      posY: Math.round(Math.min(100, Math.max(0, y)) * 10) / 10,
    });
  };

  const nameMode = state.allowsPersonalization ? "required" : state.optionalPersonalization ? "optional" : "none";
  const nameModes = [
    { id: "none", title: "من غير اسم", text: "تصميم عادي. العميل يشوف الموكاب من غير إعدادات اسم." },
    { id: "optional", title: "اسم اختياري", text: "العميل يقدر يطلبه من غير اسم. لو كتب اسم تتضاف زيادة السعر." },
    { id: "required", title: "اسم إجباري", text: "الاسم مطلوب، والسعر هو سعر التصميم نفسه من غير زيادة." },
  ];

  const typeSelector = (
    <div className="space-y-2">
      <p className="font-bold text-gray-800">الاسم على التصميم</p>
      <p className="text-sm text-gray-600">
        الصورتين موكاب للموقع. صورة الاسم والمعاينة بتتجاب من ملف الطباعة على السيرفر حسب كود التصميم.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {nameModes.map((mode) => (
          <button
            key={mode.id}
            type="button"
            onClick={() => setNameMode(mode.id)}
            className={`text-right rounded-2xl border-2 p-4 transition ${
              nameMode === mode.id
                ? "border-purple-600 bg-purple-50 ring-2 ring-purple-200"
                : "border-gray-200 bg-white hover:border-purple-200"
            }`}
          >
            <span className="block font-bold text-gray-900">{mode.title}</span>
            <span className="block text-sm text-gray-600 mt-1">{mode.text}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const fields = showNameSettings ? (
    <div className="border-2 border-purple-100 rounded-2xl p-4 bg-purple-50/40 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-bold text-purple-800">إعدادات التخصيص</p>
        {showNameSettings && (
          state.personalizationUsesDefaults !== false ? (
            <span className="text-xs font-bold rounded-full bg-emerald-100 text-emerald-800 px-3 py-1">إعدادات افتراضية مشتركة</span>
          ) : (
            <span className="text-xs font-bold rounded-full bg-amber-100 text-amber-900 px-3 py-1">إعدادات خاصة بهذا التصميم</span>
          )
        )}
      </div>
      <p className="text-sm text-gray-600">
        الافتراضي: عربي فقط، كل الخطوط، لوحة الألوان، تحريك الاسم، وتكبير الخط وتصغيره. لو فعّلت الإنجليزي نفس الاختيارات تتعلم لوحدها. المعاينة والطباعة على صورة المصدر بتاعة الكود.
      </p>
      <label className="flex items-start gap-3 rounded-2xl border border-purple-200 bg-white p-3 cursor-pointer">
        <input
          type="checkbox"
          checked={layers.some((layer) => layer.allowCustomColor)}
          onChange={(e) => applyLayers(
            layers.map((layer) => ({ ...layer, allowCustomColor: e.target.checked })),
            { customize: true }
          )}
          className="mt-1 w-5 h-5 text-purple-600 rounded"
        />
        <span>
          <span className="block font-bold text-gray-900">إظهار لوحة ألوان للعميل</span>
          <span className="block text-sm text-gray-600">العميل يشوف الألوان المحددة، ومعاها دائرة يختار منها أي لون.</span>
        </span>
      </label>
      {state.optionalPersonalization && (
        <label className="block rounded-2xl border border-fuchsia-200 bg-white p-3">
          <span className="block font-bold text-gray-900">زيادة سعر الاسم لهذا التصميم</span>
          <span className="block text-sm text-gray-600 mt-1">
            لو فاضي، بتستخدم الزيادة العامة ({globalNameSurcharge} جنيه). اكتب رقم مختلف لو التصميم ده ليه زيادة خاصة.
          </span>
          <input
            type="number"
            min="0"
            step="1"
            value={state.nameSurcharge ?? ""}
            placeholder={String(globalNameSurcharge)}
            onChange={(e) => setState({ ...state, nameSurcharge: e.target.value })}
            className="mt-3 w-full md:w-48 p-2 border-2 border-fuchsia-200 rounded-xl"
          />
        </label>
      )}
      {showNameSettings && state.personalizationUsesDefaults === false && (
        <button
          type="button"
          onClick={resetOptionalDefaults}
          className="px-3 py-2 rounded-xl border border-emerald-300 bg-white text-sm font-bold text-emerald-800"
        >
          الرجوع للإعدادات الافتراضية
        </button>
      )}
      <div className="flex flex-wrap gap-2">
        {layers.map((layer) => (
          <button
            key={layer.key}
            type="button"
            onClick={() => {
              if (!layer.enabled) {
                const defaults = createOptionalDefaultLayer(layer.key, fontCatalog);
                updateLayer(layer.key, {
                  ...defaults,
                  enabled: true,
                  allowCustomColor: layers.some((item) => item.allowCustomColor),
                });
              }
              setActiveKey(layer.key);
            }}
            className={`px-4 py-2 rounded-xl font-bold border-2 transition ${
              active?.key === layer.key
                ? "bg-purple-700 text-white border-purple-700"
                : layer.enabled
                ? "bg-white text-purple-800 border-purple-200"
                : "bg-gray-100 text-gray-500 border-gray-200"
            }`}
          >
            {layer.label} {layer.enabled ? "" : "(متوقف)"}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-red-200 p-3 space-y-3">
        <label className="flex items-center gap-2 font-bold text-red-800">
          <input
            type="checkbox"
            checked={Boolean(state.cameraExclusionEnabled)}
            onChange={(e) => setState({
              ...state,
              ...syncLegacyFromLayers(layers, { ...state, cameraExclusionEnabled: e.target.checked }),
              allowsPersonalization: state.allowsPersonalization,
              optionalPersonalization: state.optionalPersonalization,
              personalizationUsesDefaults: state.optionalPersonalization ? false : state.personalizationUsesDefaults,
            })}
          />
          تفعيل منطقة الكاميرا (منع النص)
        </label>
        <p className="text-sm text-gray-600">
          المنطقة الحمراء للمعاينة فقط، ومش بتتحط في صورة الطباعة. النص مش هيقدر يدخل فيها.
        </p>
        <label className="block text-sm text-gray-700">
          Camera Exclusion Area Height (%)
          <input
            type="number"
            min={4}
            max={40}
            step={0.5}
            value={state.cameraExclusionHeightPercent ?? 15}
            onChange={(e) => setState({
              ...state,
              ...syncLegacyFromLayers(layers, {
                ...state,
                cameraExclusionHeightPercent: clamp(e.target.value, 4, 40, 15),
              }),
              allowsPersonalization: state.allowsPersonalization,
              optionalPersonalization: state.optionalPersonalization,
              personalizationUsesDefaults: state.optionalPersonalization ? false : state.personalizationUsesDefaults,
            })}
            className="w-full mt-1 p-2 border-2 border-gray-200 rounded-xl"
          />
        </label>
        <label className="block text-sm text-gray-700">
          بداية المنطقة من فوق (%)
          <input
            type="number"
            min={0}
            max={40}
            step={0.5}
            value={state.cameraExclusionTopPercent ?? 0}
            onChange={(e) => setState({
              ...state,
              ...syncLegacyFromLayers(layers, {
                ...state,
                cameraExclusionTopPercent: clamp(e.target.value, 0, 40, 0),
              }),
              allowsPersonalization: state.allowsPersonalization,
              optionalPersonalization: state.optionalPersonalization,
              personalizationUsesDefaults: state.optionalPersonalization ? false : state.personalizationUsesDefaults,
            })}
            className="w-full mt-1 p-2 border-2 border-gray-200 rounded-xl"
          />
        </label>
      </div>

      {enabledLayers.length > 1 && (
        <div className="rounded-2xl bg-white border border-purple-200 p-3 space-y-2">
          <p className="font-bold text-purple-800">مين الطبقة اللي فوق؟</p>
          <p className="text-sm text-gray-600">اللي فوق هي اللي تغطي التانية لو الاسمين اتقابلوا.</p>
          <div className="flex flex-wrap gap-2">
            {enabledLayers.map((layer) => (
              <button
                key={layer.key}
                type="button"
                onClick={() => setTopLayer(layer.key)}
                className={`px-4 py-2 rounded-xl border-2 text-sm font-bold ${
                  layer.key === topLayerKey
                    ? "bg-purple-700 text-white border-purple-700"
                    : "bg-white text-purple-800 border-purple-200"
                }`}
              >
                {layer.label} فوق
              </button>
            ))}
          </div>
        </div>
      )}

      {active && (
        <div className="rounded-2xl bg-white/80 p-4 border border-purple-100">
          <LayerFields layer={active} onChange={(patch) => updateLayer(active.key, patch)} fonts={fontCatalog} />
        </div>
      )}
    </div>
  ) : null;

  const changeZoom = (delta) => {
    setPreviewZoom((current) => Math.min(PREVIEW_ZOOM_MAX, Math.max(PREVIEW_ZOOM_MIN, current + delta)));
  };

  const preview = (
    <div className="h-full flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-gray-800">المعاينة</p>
          <p className="text-sm text-gray-600">
            {showNameSettings
              ? resolvedPrintSource
                ? `المعاينة على صورة الطباعة بتاعة الكود. اضغط لتحريك طبقة ${active?.label || ""}.`
                : "صورة المصدر مش ظاهرة للكود ده. المعاينة مؤقتًا على موكاب الموقع."
              : "دي صورة الموقع (الموكاب). التصميم العادي مش بيرسم اسم فوق الصورة."}
          </p>
        </div>
        {canvasUrl && (
          <div className="flex items-center gap-1 shrink-0 bg-white border border-gray-200 rounded-xl p-1">
            <button
              type="button"
              onClick={() => changeZoom(-PREVIEW_ZOOM_STEP)}
              disabled={previewZoom <= PREVIEW_ZOOM_MIN}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              title="تصغير"
            >
              <ZoomOut size={18} />
            </button>
            <button
              type="button"
              onClick={() => setPreviewZoom(PREVIEW_ZOOM_DEFAULT)}
              className="min-w-[3.5rem] px-1 text-sm font-bold text-gray-700"
              title="إعادة الحجم"
            >
              {previewZoom}%
            </button>
            <button
              type="button"
              onClick={() => changeZoom(PREVIEW_ZOOM_STEP)}
              disabled={previewZoom >= PREVIEW_ZOOM_MAX}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              title="تكبير"
            >
              <ZoomIn size={18} />
            </button>
          </div>
        )}
      </div>
      {showNameSettings && enabledLayers.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {enabledLayers.map((layer) => (
            <label key={layer.key} className="text-sm text-gray-600">
              نص {layer.label}
              <input
                value={samples[layer.key] || ""}
                onChange={(e) => setSamples({ ...samples, [layer.key]: e.target.value })}
                className="w-full mt-1 p-2 border-2 border-gray-200 rounded-xl bg-white"
                dir={layer.direction}
              />
            </label>
          ))}
        </div>
      )}
      {canvasUrl ? (
        <div
          ref={previewRef}
          className={`relative mx-auto cursor-crosshair ${showNameSettings ? "" : "cursor-default"}`}
          style={{ width: `${previewZoom}%` }}
          onClick={showNameSettings ? handlePreviewClick : undefined}
        >
          <img
            src={canvasUrl}
            alt="معاينة التصميم"
            className="w-full h-auto rounded-xl border-2 border-purple-200 bg-white object-contain"
            onLoad={() => {
              if (previewRef.current?.clientWidth) setPreviewWidth(previewRef.current.clientWidth);
            }}
          />
          {showNameSettings && (
            <>
              <div
                className="absolute top-0 bottom-0 left-0 pointer-events-none"
                style={{ width: `${EDGE_EXCLUSION.leftPercent}%`, background: "rgba(220, 38, 38, 0.28)", zIndex: 8 }}
              />
              <div
                className="absolute top-0 bottom-0 right-0 pointer-events-none"
                style={{ width: `${EDGE_EXCLUSION.rightPercent}%`, background: "rgba(220, 38, 38, 0.28)", zIndex: 8 }}
              />
              <div
                className="absolute bottom-0 left-0 right-0 pointer-events-none"
                style={{ height: `${EDGE_EXCLUSION.bottomPercent}%`, background: "rgba(220, 38, 38, 0.28)", zIndex: 8 }}
              />
            </>
          )}
          {showNameSettings && state.cameraExclusionEnabled && (
            <>
              <div
                className="absolute inset-x-0 pointer-events-none"
                style={{
                  top: `${state.cameraExclusionTopPercent || 0}%`,
                  height: `${state.cameraExclusionHeightPercent || 15}%`,
                  background: "rgba(220, 38, 38, 0.28)",
                  zIndex: 8,
                }}
              />
              <div
                className="absolute pointer-events-none"
                style={{
                  top: `${(state.cameraExclusionTopPercent || 0) + (state.cameraExclusionHeightPercent || 15) * 0.14}%`,
                  left: "8%",
                  width: "22%",
                  aspectRatio: "1",
                  borderRadius: "22%",
                  background: "rgba(15, 23, 42, 0.55)",
                  boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.22)",
                  zIndex: 9,
                }}
              />
            </>
          )}
          {showNameSettings && previewWidth > 0 && enabledLayers.map((layer) => {
            const font = fontCatalog.find((item) => item.id === layer.fontId) || fontCatalog[0] || PERSONALIZATION_FONTS[1];
            const family = font.webFamily || font.cssFamily;
            const fontSizePx = ((layer.fontSizePercent || 8) / 100) * previewWidth;
            return (
              <div
                key={layer.key}
                className="personalized-name-overlay absolute pointer-events-none"
                data-font={layer.fontId}
                data-lang={layer.key}
                style={{
                  left: `${layer.posX}%`,
                  top: `${layer.posY}%`,
                  width: "max-content",
                  maxWidth: "none",
                  height: layer.boxHeightPercent > 0 ? `${layer.boxHeightPercent}%` : "auto",
                  transform: `translate(${translateX[layer.alignH] || "-50%"}, ${translateY[layer.alignV] || "-50%"}) rotate(${layer.rotation || 0}deg)`,
                  transformOrigin: "center center",
                  zIndex: layer.zIndex || 1,
                  "--personalization-font-family": `"${family}", sans-serif`,
                  "--personalization-font-size": `${fontSizePx}px`,
                  "--personalization-font-weight": String(font.weight || 400),
                  fontFamily: `"${family}", sans-serif`,
                  fontWeight: font.weight || 400,
                  fontSize: `${fontSizePx}px`,
                  color: layer.textColor || "#fff",
                  textAlign: layer.alignH,
                  direction: layer.direction,
                  letterSpacing: `${layer.letterSpacing || 0}px`,
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",
                  wordBreak: "keep-all",
                  overflowWrap: "normal",
                  overflow: layer.boxHeightPercent > 0 ? "hidden" : "visible",
                  textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                  outline: active?.key === layer.key ? "2px dashed rgba(126,34,206,0.85)" : "none",
                }}
              >
                {samples[layer.key] || LAYER_META[layer.key]?.sample || ""}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 min-h-[280px] rounded-xl border-2 border-dashed border-gray-300 bg-white/70 flex items-center justify-center text-gray-500 text-center px-4">
          {state.allowsPersonalization
            ? "ارفع صورة التخصيص (الصورة 2) عشان المعاينة تترسم على الآرتورك"
            : "ارفع صورة الموقع (الصورة 1) عشان تظهر المعاينة"}
        </div>
      )}
    </div>
  );

  if (children) {
    return (
      <>
        <aside className="min-h-0 overflow-y-auto bg-slate-100 p-4 lg:border-l border-gray-200">
          {preview}
        </aside>
        <div className="min-h-0 overflow-y-auto p-5 space-y-6">
          {typeSelector}
          {children}
          {fields}
        </div>
      </>
    );
  }

  return (
    <div className="md:col-span-2 space-y-4">
      {typeSelector}
      {fields}
      {preview}
    </div>
  );
};

export default DesignPersonalizationFields;
